import useRuntime from '@belong/providers/runtime';
import { IMetric, ROOT_SIZE, TRootSizeKeys } from '@belong/types/ui-components/Metric.types';
import {
  Container,
  Previous,
  ValueContainer,
  Currency,
  StaticValue,
  Measurement,
  Interval,
  Description
} from './Metric.styles';
import { getTimeUnitKey } from './Metric.utils';

export const METRIC_TEST_ID = {
  COST_CURRENCY: 'cost-currency',
  COST_VALUE: 'cost-value',
  COST_INTERVAL: 'cost-interval',
  MEASUREMENT_UNIT: 'measurement-unit',
  MEASUREMENT_VALUE: 'measurement-value'
};

const Metric: React.FC<IMetric> = ({
  'data-testid': testId,
  scalingRoot,
  alignBottom,
  priceUnit,
  value,
  description,
  interval,
  previousValue,
  measurementUnit,
  ariaLabel
}) => {
  const { isMobileViewport } = useRuntime();

  const exceedsThreshold = (currentRootKey: TRootSizeKeys, thresholdRootKey: TRootSizeKeys): boolean => {
    const rootKeysArray: string[] = Object.keys(ROOT_SIZE.desktop);

    const currentRootKeyIdx = rootKeysArray.indexOf(currentRootKey);
    const thresholdIndex = rootKeysArray.indexOf(thresholdRootKey);

    return currentRootKeyIdx > thresholdIndex;
  };

  const responsivePreviousSuffix = () =>
    exceedsThreshold(scalingRoot, isMobileViewport ? 'XXL' : 'L') ? ', now:' : '';

  return (
    <Container
      scalingRoot={scalingRoot}
      alignBottom={alignBottom}
      aria-label={ariaLabel}
      data-testid={testId}
      className="metric-container"
    >
      {previousValue && (
        // if there is a previous value (i.e. there is a current discount) compile the string for the previous value
        <Previous as="span" className="metric-previous" scalingRoot={scalingRoot}>
          {`Was ${priceUnit || ''}${previousValue}${measurementUnit || ''}${
            interval ? `/${getTimeUnitKey(interval.toLowerCase())}` : ''
          }${responsivePreviousSuffix()}`}
        </Previous>
      )}
      <ValueContainer scalingRoot={scalingRoot} hasPrevious={!!previousValue}>
        {priceUnit && (
          <Currency
            as="span"
            scalingRoot={scalingRoot}
            exceedsThreshold={!!exceedsThreshold(scalingRoot, isMobileViewport ? 'XXL' : 'L')}
            data-testid={METRIC_TEST_ID.COST_CURRENCY}
          >
            {priceUnit}
          </Currency>
        )}
        <StaticValue
          as="span"
          data-testid={measurementUnit ? METRIC_TEST_ID.MEASUREMENT_VALUE : METRIC_TEST_ID.COST_VALUE}
        >
          {value}
        </StaticValue>
        {measurementUnit && (
          <Measurement as="span" data-testid={METRIC_TEST_ID.MEASUREMENT_UNIT}>
            {measurementUnit}
          </Measurement>
        )}
        {interval && (
          <Interval as="span" scalingRoot={scalingRoot} data-testid={METRIC_TEST_ID.COST_INTERVAL}>
            /{getTimeUnitKey(interval.toLowerCase())}
          </Interval>
        )}
      </ValueContainer>
      {description && (
        <Description as="span" scalingRoot={scalingRoot}>
          {description}
        </Description>
      )}
    </Container>
  );
};

Metric.displayName = 'Metric';
export default Metric;
